import { useState, useEffect } from "react";
import { useMutation } from "@tanstack/react-query";
import { useSingleFlight } from "@/hooks/useSingleFlight";
import { MessageSquare, X, Check } from "lucide-react";
import html2canvas from "html2canvas";
import { useAuth } from "@/contexts/AuthContext";
import { apiRequest } from "@/lib/queryClient";
import { SAGE } from "@shared/brand";

// Lets other surfaces (e.g. the per-section thumbs-down, s102 DB-16 1c) open the
// widget pre-filled. Dispatches a window event the mounted widget listens for.
const OPEN_FEEDBACK_EVENT = "oe:open-feedback";
export function openFeedbackPanel(detail?: { type?: string; body?: string }) {
  window.dispatchEvent(new CustomEvent(OPEN_FEEDBACK_EVENT, { detail }));
}

// Friends & Family feedback widget (s102 DB-16). Floating sage pill, bottom-right,
// every authenticated page, F&F-only. Friction-minimized per D-99.7: type defaults to
// "confusing", nothing is required (one word sends). Screenshot auto-capture lands in
// 1b. Removed at launch (the F&F flag is cleared in the pre-launch cleanup → the pill
// disappears for everyone).

const FEEDBACK_TYPES = [
  { value: "confusing", label: "Confusing" },
  { value: "bug", label: "Bug" },
  { value: "suggestion", label: "Suggestion" },
  { value: "other", label: "Other" },
] as const;


export function FeedbackWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<string>("confusing");
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  // Auto-capture the current page (s102 DB-16 1b, re-added DB-33). The widget excludes
  // itself via the data-feedback-root marker so the shot shows the page, not the panel.
  // Scale 0.5 + JPEG keeps the base64 modest. Capture failures (e.g. a CORS-tainted
  // canvas) fail silently — the note still sends without a screenshot. Runs on every
  // panel-open path (pill click + the thumbs-down event below).
  const captureScreenshot = async () => {
    setCapturing(true);
    try {
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        useCORS: true,
        logging: false,
        ignoreElements: (el) => el.getAttribute?.("data-feedback-root") === "true",
      });
      setScreenshot(canvas.toDataURL("image/jpeg", 0.7));
    } catch {
      setScreenshot(null);
    } finally {
      setCapturing(false);
    }
  };

  const openPanel = () => {
    setOpen(true);
    captureScreenshot();
  };

  // Open pre-filled when another surface asks (thumbs-down → "tell us more?"). Hook
  // is registered unconditionally (before the F&F early-return) to satisfy the rules
  // of hooks; a non-F&F user still renders null, so the panel can't actually show.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      setType(typeof detail.type === "string" ? detail.type : "confusing");
      setBody(typeof detail.body === "string" ? detail.body : "");
      setSent(false);
      setOpen(true);
      captureScreenshot();
    };
    window.addEventListener(OPEN_FEEDBACK_EVENT, handler);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, handler);
  }, []);

  // F&F-gated. Non-F&F users (and logged-out) never see the pill. The server also
  // gates POST /api/feedback on is_f_and_f, so this is defence-in-depth, not the only
  // gate.
  // D-170.6: converted from a raw apiRequest. Class A — each fire INSERTs a feedback row and
  // sends a notification email (routes.ts:212-250), so a double-submit is a duplicate report.
  // Registered above the F&F early-return, with the useEffect, for the rules of hooks.
  const feedbackFlight = useSingleFlight();
  const feedbackMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) =>
      await apiRequest("POST", "/api/feedback", payload),
  });

  if (!user?.isFAndF) return null;

  const reset = () => {
    setType("confusing");
    setBody("");
    setSent(false);
    setScreenshot(null);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const ok = await feedbackFlight.run(() =>
        feedbackMutation.mutateAsync({
          kind: "widget",
          type,
          body,
          pageUrl: window.location.href,
          userAgent: navigator.userAgent,
          screenshot,
        }),
      );
      if (!ok) return; // duplicate fire — the original submit owns the confirmation
      setSent(true);
      setTimeout(() => {
        setOpen(false);
        reset();
      }, 1200);
    } catch {
      // Keep the panel open so the note isn't lost; a transient failure is rare at F&F scale.
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        data-feedback-root="true"
        onClick={openPanel}
        aria-label="Send feedback"
        data-testid="feedback-pill"
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-white shadow-lg transition-transform hover:scale-105"
        style={{ backgroundColor: SAGE[600] }}
      >
        <MessageSquare className="h-4 w-4" />
        Feedback
      </button>
    );
  }

  return (
    <div
      data-feedback-root="true"
      className="fixed bottom-5 right-5 z-50 w-[320px] rounded-xl border border-warm-stone bg-warm-white shadow-2xl"
      data-testid="feedback-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-warm-stone">
        <span className="text-sm font-semibold" style={{ color: SAGE[600] }}>Share feedback</span>
        <button
          type="button"
          onClick={() => { setOpen(false); reset(); }}
          aria-label="Close feedback"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {sent ? (
        <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
          <Check className="h-6 w-6" style={{ color: SAGE[600] }} />
          <p className="text-sm text-foreground">Thank you — sent!</p>
        </div>
      ) : (
        <div className="px-4 py-3 space-y-3">
          <div className="flex flex-wrap gap-1.5">
            {FEEDBACK_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setType(t.value)}
                data-testid={`feedback-type-${t.value}`}
                className="rounded-full px-3 py-1 text-xs font-medium border transition-colors"
                style={
                  type === t.value
                    ? { backgroundColor: SAGE[600], color: "#fff", borderColor: SAGE[600] }
                    : { backgroundColor: "transparent", color: "#6B7280", borderColor: "#E2E0D8" }
                }
              >
                {t.label}
              </button>
            ))}
          </div>

          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="What's on your mind? (optional — even one word helps)"
            rows={4}
            data-testid="feedback-body"
            className="w-full resize-none rounded-md border border-warm-stone bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
            style={{ ["--tw-ring-color" as any]: SAGE[600] }}
          />

          {capturing ? (
            <p className="text-xs text-muted-foreground" data-testid="feedback-shot-capturing">Capturing screenshot…</p>
          ) : screenshot ? (
            <div className="relative rounded-md border border-warm-stone overflow-hidden" data-testid="feedback-shot-preview">
              <img src={screenshot} alt="Page screenshot" className="w-full max-h-32 object-cover object-top" />
              <button
                type="button"
                onClick={() => setScreenshot(null)}
                aria-label="Remove screenshot"
                data-testid="feedback-shot-remove"
                className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : null}

          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="feedback-submit"
            className="w-full rounded-md py-2 text-sm font-medium text-white transition-opacity disabled:opacity-60"
            style={{ backgroundColor: SAGE[600] }}
          >
            {submitting ? "Sending…" : "Send feedback"}
          </button>
        </div>
      )}
    </div>
  );
}
